import React, { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import AnimatedCounter from '../components/AnimatedCounter';
import LazyImage from '../components/LazyImage';
import SEO from '../components/SEO';
import { Database, Brain, CheckCircle, Rocket, ArrowRight, Award, Users, Target } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

const Homepage = () => {
  // Autoplay plugin configuration
  const autoplayOptions = {
    delay: 5000,
    stopOnInteraction: false,
    stopOnMouseEnter: true,
  };

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      align: 'center',
      duration: 40,
    },
    [Autoplay(autoplayOptions)]
  );

  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index) => {
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  const lifecycleStages = [
    {
      icon: Database,
      title: 'Data Preparation',
      description: 'High-fidelity annotation for training data that meets production standards',
      link: '/solutions#annotation'
    },
    {
      icon: CheckCircle,
      title: 'Model Evaluation',
      description: 'Comprehensive QA, benchmarking, and error detection before deployment',
      link: '/solutions#validate'
    },
    {
      icon: Brain,
      title: 'Model Training',
      description: 'Human-in-the-loop continuous improvement and RLHF for optimal performance',
      link: '/solutions#genai'
    },
    {
      icon: Rocket,
      title: 'Production & Scaling',
      description: 'Seamless integration and workforce scalability for enterprise deployment',
      link: '/solutions#operations'
    }
  ];

  const featuredCaseStudies = [
    {
      title: 'SliceVault',
      industry: 'Medical AI',
      logo: '/slicevault-logo.jpg',
      result: '10,000+ medical images with 99%+ precision',
      testimonial: 'Orboroi delivered the level of precision and consistency we needed for medical AI.',
      author: 'Lars Edenbrant',
      image: 'https://images.pexels.com/photos/7089298/pexels-photo-7089298.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      link: '/case-study-slicevault'
    },
    {
      title: 'Spiideo AB',
      industry: 'Sports AI',
      logo: '/spiideo-logo.jpg',
      result: '30,000+ sports videos annotated with 99%+ accuracy',
      testimonial: 'After working together for more than a year, we have built a strong foundation of collaboration and trust.',
      author: 'Daniel Milesson',
      image: 'https://images.unsplash.com/photo-1613758403772-268da019247a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxzcG9ydHMlMjB2aWRlbyUyMHRyYWNraW5nJTIwcGxheWVyJTIwZGV0ZWN0aW9uJTIwb2JqZWN0JTIwdHJhY2tpbmclMjBhbmFseXRpY3N8ZW58MHx8fHwxNzc0Mjk4NTkxfDA&ixlib=rb-4.1.0&q=85',
      link: '/case-study-spiideo'
    },
    {
      title: 'Katam Technologies',
      industry: 'Forestry AI',
      logo: '/katam-logo.jpg',
      result: '10,000+ images with 99%+ accuracy for tree detection',
      testimonial: 'They\'ve shown great adaptability whilst giving valuable input on areas of improvement.',
      author: 'Axel Persson',
      image: 'https://images.unsplash.com/photo-1728932828917-db6f16a257dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHw0fHxhZXJpYWwlMjBmb3Jlc3RyeSUyMGRyb25lJTIwdHJlZSUyMGRldGVjdGlvbiUyMG1hcHBpbmclMjBsaWRhcnxlbnwwfHx8fDE3NzQyOTg1OTJ8MA&ixlib=rb-4.1.0&q=85',
      link: '/case-study-katam'
    }
  ];

  const breadcrumbs = [
    { name: "Home", url: "/" }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO
        title="Data Certainty for AI - Professional AI Data Operations"
        description="Orboroi delivers production-ready AI data services: annotation, validation, GenAI evaluation, and model operations. Achieve data certainty across your AI lifecycle."
        canonical="/"
        type="website"
        breadcrumbs={breadcrumbs}
      />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1750969185331-e03829f72c7d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxuZXVyYWwlMjBuZXR3b3JrJTIwQUklMjBhYnN0cmFjdCUyMHRlY2hub2xvZ3klMjBibHVlJTIwZGlnaXRhbHxlbnwwfHx8fDE3NzQyOTg1OTd8MA&ixlib=rb-4.1.0&q=85"
            alt="AI Neural Networks"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/70 to-black"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Achieve <span className="text-[#379F7F]">Data Certainty</span> Across the Entire AI Model Lifecycle
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              From training data to production deployment, Orboroi delivers high-fidelity annotation, 
              model evaluation, and human-in-the-loop services—ensuring your AI models perform 
              reliably in real-world environments.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                size="lg"
                className="bg-[#379F7F] text-black hover:bg-[#379F7F]/90 font-semibold text-lg px-8 py-6"
              >
                <Link to="/process">Start Your Lifecycle Journey</Link>
              </Button>
              <Button 
                asChild
                size="lg"
                variant="outline"
                className="border-[#379F7F] text-[#379F7F] hover:bg-[#379F7F] hover:text-black font-semibold text-lg px-8 py-6"
              >
                <Link to="/case-studies">See Our Work</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Key Metrics Section */}
      <section className="py-12 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-[#379F7F] transition-all duration-300">
              <Award className="w-12 h-12 text-[#379F7F] mx-auto mb-4" />
              <div className="text-5xl font-bold text-[#379F7F] mb-2">
                <AnimatedCounter end={99} suffix="%" />
              </div>
              <p className="text-gray-300 text-lg">First-Batch Acceptance Rate</p>
            </div>
            <div className="text-center p-8 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-[#379F7F] transition-all duration-300">
              <Target className="w-12 h-12 text-[#379F7F] mx-auto mb-4" />
              <div className="text-5xl font-bold text-[#379F7F] mb-2">
                <AnimatedCounter end={100} suffix="+" />
              </div>
              <p className="text-gray-300 text-lg">Projects Delivered Across AI Model Lifecycle</p>
            </div>
            <div className="text-center p-8 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-[#379F7F] transition-all duration-300">
              <Users className="w-12 h-12 text-[#379F7F] mx-auto mb-4" />
              <div className="text-5xl font-bold text-[#379F7F] mb-2">
                <AnimatedCounter end={500} suffix="k+" />
              </div>
              <p className="text-gray-300 text-lg">Annotated Assets for Production-Ready Models</p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Model Lifecycle Preview */}
      <section className="py-12 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Complete <span className="text-[#379F7F]">AI Model Lifecycle</span> Coverage
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We support your AI models at every critical stage, from initial data preparation 
              to production deployment and beyond.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {lifecycleStages.map((stage, index) => (
              <Card 
                key={index}
                className="bg-gray-800 border-gray-700 hover:border-[#379F7F] transition-all duration-300 hover:scale-105 cursor-pointer group"
              >
                <CardHeader>
                  <stage.icon className="w-12 h-12 text-[#379F7F] mb-4 group-hover:scale-110 transition-transform duration-300" />
                  <CardTitle className="text-white">{stage.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-300">
                    {stage.description}
                  </CardDescription>
                  <Link 
                    to={stage.link}
                    className="inline-flex items-center text-[#379F7F] mt-4 hover:underline group-hover:gap-2 transition-all duration-300"
                  >
                    Learn more <ArrowRight className="ml-1 w-4 h-4" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Carousel */}
      <section className="py-12 bg-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Trusted by <span className="text-[#379F7F]">Industry Leaders</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              See how we've helped organizations achieve data certainty and deploy 
              production-ready AI models across various industries.
            </p>
          </div>

          {/* Modern Carousel Container */}
          <div className="relative mb-12">
            {/* Carousel Viewport */}
            <div className="overflow-hidden rounded-2xl" ref={emblaRef}>
              <div className="flex">
                {featuredCaseStudies.map((study, index) => (
                  <div key={index} className="flex-[0_0_100%] min-w-0">
                    <Link to={study.link} className="block">
                      <div className="relative h-[450px] md:h-[500px] overflow-hidden group cursor-pointer">
                        {/* Background Image with Parallax Effect */}
                        <div className="absolute inset-0">
                          <LazyImage 
                            src={study.image}
                            alt={study.title}
                            className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-105"
                          />
                          {/* Multi-layer Gradient Overlay */}
                          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40"></div>
                          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent"></div>
                        </div>

                        {/* Content Container */}
                        <div className="relative h-full flex items-end">
                          <div className="container mx-auto px-6 md:px-12 pb-8 md:pb-12">
                            <div className="max-w-3xl">
                              {/* Industry Badge */}
                              <div className="inline-flex items-center gap-2 mb-4">
                                <div className="h-px w-12 bg-[#379F7F]"></div>
                                <span className="text-[#379F7F] font-semibold text-sm uppercase tracking-wider">
                                  {study.industry}
                                </span>
                              </div>

                              {/* Company Logo */}
                              <div className="mb-4">
                                <div className="inline-block bg-white/95 backdrop-blur-sm p-2.5 rounded-lg shadow-2xl">
                                  <img 
                                    src={study.logo}
                                    alt={`${study.title} logo`}
                                    className="h-8 md:h-10 w-auto"
                                  />
                                </div>
                              </div>

                              {/* Title */}
                              <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 leading-tight">
                                {study.title}
                              </h3>

                              {/* Result Stats */}
                              <p className="text-lg md:text-xl text-[#379F7F] font-semibold mb-4">
                                {study.result}
                              </p>

                              {/* Testimonial */}
                              <blockquote className="border-l-4 border-[#379F7F] pl-4 mb-5">
                                <p className="text-base md:text-lg text-gray-200 italic leading-relaxed mb-2">
                                  "{study.testimonial}"
                                </p>
                                <cite className="text-[#379F7F] font-medium not-italic text-sm">
                                  — {study.author}
                                </cite>
                              </blockquote>

                              {/* CTA Button */}
                              <Button 
                                className="bg-[#379F7F] text-black hover:bg-[#379F7F]/90 font-semibold text-base md:text-lg px-6 py-5 rounded-full shadow-2xl hover:shadow-[#379F7F]/50 transition-all duration-300 group/btn"
                              >
                                Read Full Case Study
                                <ArrowRight className="w-5 h-5 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>

            {/* Modern Pagination Dots */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3 z-20">
              {featuredCaseStudies.map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollTo(index)}
                  className={`transition-all duration-500 rounded-full ${
                    index === selectedIndex
                      ? 'bg-[#379F7F] w-12 h-2 shadow-lg shadow-[#379F7F]/50'
                      : 'bg-white/40 hover:bg-white/60 w-2 h-2'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          <div className="text-center">
            <Button 
              asChild
              size="lg"
              className="bg-[#379F7F] text-black hover:bg-[#379F7F]/90 font-semibold"
            >
              <Link to="/case-studies">View All Case Studies</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Achieve <span className="text-[#379F7F]">Data Certainty</span>?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Get a comprehensive audit of your data pipeline and discover opportunities 
              to improve model performance and reduce deployment risk.
            </p>
            <Button 
              asChild
              size="lg"
              className="bg-[#379F7F] text-black hover:bg-[#379F7F]/90 font-semibold text-lg px-8 py-6"
            >
              <Link to="/contact">Get a Data Certainty Audit</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Homepage;
